import ColumnFilterContainer from '@/components/ColumnFilters/ColumnFilterContainer';
import { useGetSusQuery } from '@/features/api/endpoints/suEndpoints';
import type { ColumnFilterProps } from '@/models/flowFilter.type';
import { MultiSelect } from '@mantine/core';
import { useTranslate } from '@tolgee/react';
import { useMemo } from 'react';

const SUS_FIELDS = ['id', 'nom'] as const;
// Le flow plafonne la taille de page ; la liste des SUS tient largement dans cette limite.
const SUS_PAGE_SIZE = 1000;

/** Filtre à choix multiples sur les services d'urgence ; les valeurs sont leurs identifiants. */
const SusColumnFilter = ({ label, value, onChange }: ColumnFilterProps) => {
  const { t } = useTranslate();
  const { data, isFetching, isError } = useGetSusQuery({
    page: 1,
    pageSize: SUS_PAGE_SIZE,
    fields: SUS_FIELDS,
    orderBy: 'nom asc',
  });

  const selected = useMemo(() => (value?.kind === 'select' ? value.values : []), [value]);

  const options = useMemo(() => {
    const known = new Map(
      (data?.rows ?? []).map(row => [String(row.id), row.nom ?? String(row.id)]),
    );
    // Un identifiant venu de l'URL reste affiché même si la liste n'est pas (encore) chargée.
    selected.forEach(id => {
      if (!known.has(id)) known.set(id, id);
    });
    return Array.from(known, ([optionValue, optionLabel]) => ({
      value: optionValue,
      label: optionLabel,
    }));
  }, [data, selected]);

  return (
    <ColumnFilterContainer onReset={() => onChange(undefined)}>
      <MultiSelect
        searchable
        clearable
        // Largeur fixe : sans elle, le champ (et la fenêtre du filtre) s'élargit à chaque flux
        // sélectionné ; les valeurs passent alors à la ligne.
        w={{ base: 240, sm: 320 }}
        label={label}
        data={options}
        value={selected}
        disabled={isFetching && options.length === 0}
        error={isError ? t('common.filters.sus.error') : undefined}
        nothingFoundMessage={t('common.filters.sus.empty')}
        comboboxProps={{ withinPortal: false }}
        onChange={values => onChange(values.length > 0 ? { kind: 'select', values } : undefined)}
      />
    </ColumnFilterContainer>
  );
};

export default SusColumnFilter;
